import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';

import { AlertService } from '../../commons/services/alert.service';
import { AppState } from '../reducers';
import * as UserActions from '../actions/user.actions';
import { LaravelUserService } from '../../commons/services/laravel/laravel-user.service';
import { NewUsersComponent } from '../../modules/home/modules/users/components/new-users/new-users.component';
import { getUserDialogId, getUsersTableState } from '../selectors/user.selectors';
import { getTenants } from '../selectors/tenant.selectors';
import { DialogComponent } from '../../modules/shared/components/dialog/dialog.component';

@Injectable()
export class UsersEffects {

  /** Effetto per gestire gli errori */
  error$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.deleteUserFailed, UserActions.saveUserFailed),
        tap(({ error }) => {
          if (error) {
            this.alertService.showErrorMessage('Errore', error);
          }
        })
      ),
    { dispatch: false }
  );

  /** Caricamento utenti filtrati per Admin e Manager */
  loadUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.loadUsers),
      switchMap(({ page, perPage, order, direction, filters, includes }) => {
        return this.usersService
          .list(page, perPage, order, direction, filters, includes)
          .pipe(
            map((result) => {
              //console.log("🔍 Risultato API:", result.data); // ✅ Debug qui
              return UserActions.loadUsersCompleted({
                users: result.data,
                currentPage: page,
                total: result.total,
                perPage,
                order,
                direction,
                filters,
                includes
              });
            }),

            catchError((error) => of(UserActions.loadUsersFailed({ error })))
          );
      })
    )
  );

  /** Cambia pagina della lista utenti */
  changePage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.changePage),
      withLatestFrom(this.store$.select(getUsersTableState)),
      map(([{ page, size }, { order, direction, filters, includes }]) =>
        UserActions.loadUsers({
          page: page,
          perPage: size,
          order,
          direction,
          filters,
          includes,
        })
      )
    )
  );

  /** Cambia ordinamento della lista utenti */
  changeSort$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.changeSort),
      withLatestFrom(this.store$.select(getUsersTableState)),
      map(([action, { currentPage, perPage, filters, includes }]) =>
        UserActions.loadUsers({
          page: currentPage,
          perPage: perPage,
          order: action.order,
          direction: action.direction,
          filters,
          includes,
        })
      )
    )
  );

  /** Cambia i filtri applicati alla lista utenti */
  changeFilters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.changeFilters),
      withLatestFrom(this.store$.select(getUsersTableState)),
      map(([{ filters }, { currentPage, perPage, order, direction, includes }]) =>
        UserActions.loadUsers({
          page: currentPage,
          perPage,
          order,
          direction,
          filters,
          includes,
        })
      )
    )
  );

  /** Apertura dialog per la modifica di un utente */
  editUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.editUser),
      withLatestFrom(this.store$.select(getTenants)),
      map(([action, tenants]) => {
        let dialogRef = this.dialog.open(NewUsersComponent, {
          data: { user: action.user, tenants },
          panelClass: 'full-screen-dialog'
        });
        return UserActions.userDialogOpened({ dialogId: dialogRef.id });
      })
    )
  );


/** Salvataggio utente */
saveUser$ = createEffect(() =>
  this.actions$.pipe(
    ofType(UserActions.saveUser),
    // tap(({ user }) => console.log("🚀 User prima di upsert:", user)), // 🔍 Debug
    mergeMap(({ user }) =>
      this.usersService.upsert(user.toDTO()).pipe(
        map((result) => UserActions.saveUserCompleted({ user: result })),
        catchError((error) => of(UserActions.saveUserFailed({ error })))
      )
    )
  )
);


  /** Mostra messaggio di successo dopo il salvataggio */
  onSaveCompleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.saveUserCompleted),
      tap(({ user }) =>
        this.alertService.showConfirmMessage(`Utente ${user.name} salvato con successo`)
      ),
      map(() => UserActions.closeUserDialog())
    )
  );

  /** Eliminazione utente */
  deleteUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.deleteUser),
      switchMap(({ user }) =>
        this.alertService
          .showConfirmDialog(
            'Conferma eliminazione',
            `Sei sicuro di voler eliminare l'utente ${user.name} ${user.surname}?`
          )
          .pipe(
            mergeMap((confirm) =>
              confirm
                ? this.usersService.delete(user.id).pipe(
                    map(() => UserActions.deleteUserCompleted({ user })),
                    catchError((error) => of(UserActions.deleteUserFailed({ error })))
                  )
                : of(UserActions.deleteUserCancelled())
            )
          )
      )
    )
  );

  /** Mostra messaggio di successo dopo l'eliminazione */
  onDeleteCompleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.deleteUserCompleted),
      tap(({ user }) =>
        this.alertService.showConfirmMessage(`Utente ${user.name} eliminato con successo`)
      ),
      tap(() => UserActions.closeUserDialog())
    ), { dispatch: false }
  );

  /** Ricarica lista dopo salvataggio */
  reloadAfterSave$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.saveUserCompleted),
      withLatestFrom(this.store$.select(getUsersTableState)),
      map(([_, { currentPage, perPage, order, direction, filters, includes }]) =>
        UserActions.loadUsers({
          page: currentPage,
          perPage,
          order,
          direction,
          filters,
          includes,
        })
      )
    )
  );

  /** Ricarica lista dopo eliminazione */
  reloadAfterDelete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.deleteUserCompleted),
      withLatestFrom(this.store$.select(getUsersTableState)),
      map(([_, { currentPage, perPage, order, direction, filters, includes }]) =>
        UserActions.loadUsers({
          page: currentPage,
          perPage,
          order,
          direction,
          filters,
          includes,
        })
      )
    )
  );


  requestCloseDialog$ = createEffect(() =>
      this.actions$.pipe(
        ofType(UserActions.requestCloseUsersDialog),
        switchMap(() => {
          const confirmDialogRef = this.dialog.open(DialogComponent, {
            disableClose: false
          });

          // Imposta il messaggio del dialog
          confirmDialogRef.componentInstance.title = "Chiudi";
          confirmDialogRef.componentInstance.confirmMessage =
            "Ci sono modifiche non salvate. Sei sicuro di voler chiudere?";

          return confirmDialogRef.afterClosed().pipe(
            map(result =>
              result
                ? UserActions.confirmCloseUsersDialog()
                : UserActions.cancelCloseUsersDialog()
            )
          );
        })
      )
    );


    confirmCloseDialog$ = createEffect(() =>
      this.actions$.pipe(
        ofType(UserActions.confirmCloseUsersDialog),
        tap(() => UserActions.closeUserDialog())
      ), { dispatch: false }
    );

    cancelCloseDialog$ = createEffect(() =>
      this.actions$.pipe(
        ofType(UserActions.cancelCloseUsersDialog),
        map(() => {
          // Se l'utente annulla mantiene il dialog aperto
          return { type: 'NO_ACTION' };
        })
      )
    );

    closeDialog$ = createEffect(
      () =>
        this.actions$.pipe(
          ofType(UserActions.closeUserDialog),
          withLatestFrom(this.store$.select(getUserDialogId)),
          tap(([_, dialogId]) => {
            if (dialogId) {
              this.dialog.getDialogById(dialogId).close();
            }
          })
        ),
      { dispatch: false }
    );


  constructor(
    private actions$: Actions,
    private store$: Store<AppState>,
    private usersService: LaravelUserService,
    private dialog: MatDialog,
    private alertService: AlertService
  ) {}
}
